import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {SelectIcon} from './SelectV2';
import {BaseSyntheticEvent} from 'react';
import ReactCountryFlag from 'react-country-flag';
import {CourseEnquiryPopupType} from './types';

type PhoneProps = CourseEnquiryPopupType & {
  mobileNumberFocused: boolean;
  setMobileNumberFocused: React.Dispatch<React.SetStateAction<boolean>>;
  countriesJson: {
    countryCode: string;
    countryName: string;
    countryCodeAlpha: string;
  }[];
};

const Phone = (props: PhoneProps) => {
  const {
    formik,
    menuOpen,
    mobileNumberFocused,
    setMobileNumberFocused,
    countriesJson,
  } = props;

  const error =
    formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber);

  return (
    <Box
      sx={{
        display: {xxs: 'block', md: 'flex'},
        justifyContent: 'space-between',
        mt:
          formik.touched.currentLocationCoursePage &&
          formik.errors.currentLocationCoursePage?.length
            ? 0.875
            : 3,
      }}
    >
      <FormControl
        id="phoneNumber"
        key="phoneNumber"
        error={error}
        sx={{
          display: 'block',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            color: '#333',
            fontFamily: 'Poppins',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '18px',
          }}
        >
          Mobile Number&#42;
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            border: '1px solid',
            borderColor:
              error ||
              (formik.touched.countryCode && Boolean(formik.errors.countryCode))
                ? 'red'
                : mobileNumberFocused
                ? '#33333390'
                : '#e0e0e0',
            borderRadius: 8,
            overflow: 'hidden',
            mt: 1,
            height: 40,
            paddingLeft: 1.5,
            ':hover': {
              borderColor: '#33333390',
            },
            maxWidth: 390,
          }}
        >
          <Select
            name="countryCode"
            value={formik.values.countryCode}
            onChange={formik.handleChange}
            displayEmpty
            variant="standard"
            disableUnderline
            onFocus={() => {
              setMobileNumberFocused(true);
            }}
            onBlur={() => {
              setMobileNumberFocused(false);
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  marginTop: '10px',
                  maxHeight: '90px',
                  border: '1px solid #e0e0e0',
                },
                sx: {
                  marginLeft: {xs: '40px', md: '125px'},
                  width: {xs: '250px', md: 'fit-content'},
                },
              },
              disableScrollLock: true,
              // anchorOrigin: {
              //   vertical: 'top', // Open from the top
              //   horizontal: 'left',
              // },
              // transformOrigin: {
              //   vertical: 'bottom', // Expand upwards
              //   horizontal: 'left',
              // },
            }}
            IconComponent={({...iconProps}) => (
              <SelectIcon {...iconProps} open={menuOpen} />
            )}
            renderValue={selected => {
              const country = countriesJson.find(
                item => item.countryCode === selected
              );
              return (
                country && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <ReactCountryFlag
                      countryCode={country.countryCodeAlpha}
                      svg
                      style={{
                        width: 24,
                        height: 16,
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 18,
                        color: '#333333',
                        paddingLeft: 0.5,
                      }}
                    >
                      {country.countryCode}
                    </Typography>
                  </Box>
                )
              );
            }}
            sx={{
              paddingRight: 2,
              borderRight: '1px solid #e0e0e0',
              height: '26px',
              '.MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white !important',
              },
              '.MuiSelect-select.MuiSelect-filled, .MuiSelect-select.MuiSelect-outlined':
                {
                  backgroundColor: 'white !important',
                },
              '.MuiMenuItem-root': {
                ':hover': {
                  backgroundColor: '#91B87929',
                },
                '&.Mui-selected': {
                  backgroundColor: '#91B87929',
                  ':hover': {
                    backgroundColor: '#91B87929',
                  },
                },
              },
            }}
          >
            {countriesJson.map(country => (
              <MenuItem key={country.countryCode} value={country.countryCode}>
                <ReactCountryFlag
                  countryCode={country.countryCodeAlpha}
                  svg
                  style={{
                    marginRight: '4px',
                    width: '27px',
                    height: '18px',
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '18px',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '18px',
                    marginRight: '4px',
                  }}
                >
                  {country.countryName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '18px',
                    marginRight: 8,
                  }}
                >
                  {country.countryCode}
                </Typography>
              </MenuItem>
            ))}
          </Select>

          <TextField
            type="text"
            name="phoneNumber"
            placeholder="Enter your Mobile Number"
            value={formik.values.phoneNumber}
            onChange={(e: BaseSyntheticEvent) => {
              const cleanedValue = e.target?.value?.replace(/\D/g, '');
              if (cleanedValue.length > 10) {
                return;
              }
              e.target.value = cleanedValue;
              formik.handleChange(e);
            }}
            onFocus={() => {
              setMobileNumberFocused(true);
            }}
            variant="standard"
            onBlur={async e => {
              setMobileNumberFocused(false);
              formik.handleBlur(e);

              const phoneNumber = formik.values.phoneNumber;
              const countryCode = formik.values.countryCode;
              if (countryCode === '+91') {
                if (phoneNumber && phoneNumber.length !== 10) {
                  formik.setFieldError(
                    'phoneNumber',
                    'Phone number must be 10 digits'
                  );
                } else {
                  formik.setFieldError('phoneNumber', '');
                }
              }
              await formik.validateField('phoneNumber');
            }}
            InputProps={{
              disableUnderline: true,
              sx: {
                paddingLeft: 1,
                '& .MuiInputBase-input': {
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: '16px',
                },
              },
            }}
            fullWidth
            onWheel={(e: BaseSyntheticEvent) => e.target?.blur()}
          />
        </Box>

        {error && (
          <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default Phone;
